/**
 * SLAM capture recorder — turns "it drifted in that room" into a dataset.
 *
 * Records, on device: downscaled JPEG frames, the IMU stream, and the tracker's
 * pose/telemetry output, all on one clock. Exports a single JSON dataset that
 * the replay harness feeds back through the *same* tracking code at your desk.
 *
 * Frames dominate the size, so they are downscaled and JPEG-compressed hard, and
 * the recording is capped. This is a debugging instrument, not an archive.
 */

import type { FrameProvider } from '../spatial/FrameProvider.js';
import type { DevicePose } from '../spatial/DevicePose.js';
import { telemetry, type TelemetrySample, type TelemetrySummary } from '../spatial/telemetry.js';
import { API_URL, UPLOAD_TOKEN } from '../config.js';

export const DATASET_VERSION = 1;

export interface DatasetFrame {
  /** ms since recording start. */
  t: number;
  /** JPEG data URL. */
  image: string;
}

export interface DatasetImu {
  t: number;
  quat: { x: number; y: number; z: number; w: number };
  yaw: number;
  pitch: number;
  roll: number;
}

export interface SlamDataset {
  version: number;
  recordedAt: string;
  backend: string;
  device: {
    ua: string;
    screen: { w: number; h: number };
    dpr: number;
  };
  frameSize: { width: number; height: number };
  frames: DatasetFrame[];
  imu: DatasetImu[];
  telemetry: TelemetrySample[];
  summary: TelemetrySummary | null;
  notes?: string;
}

export interface RecorderOptions {
  /** Longest edge of recorded frames. Smaller = smaller dataset. */
  maxEdge?: number;
  jpegQuality?: number;
  /** Frames per second to record. */
  fps?: number;
  /** Hard cap so a forgotten recording cannot eat all memory. */
  maxFrames?: number;
  /**
   * Hard cap on IMU samples (30 Hz). Needed separately: frames are capped, but
   * if `drawTo` keeps failing no frame is ever pushed, so the frame cap never
   * trips and the IMU array would grow for as long as the page lives.
   */
  maxImu?: number;
}

export class SlamRecorder {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private frames: DatasetFrame[] = [];
  private imu: DatasetImu[] = [];
  private timer: number | null = null;
  private imuTimer: number | null = null;
  private t0 = 0;
  private recording = false;
  private opts: Required<RecorderOptions>;
  private w = 0;
  private h = 0;

  constructor(opts: RecorderOptions = {}) {
    this.opts = {
      // Match AlvaARBackend's PROC_LONG_EDGE. If the dataset is recorded at a
      // different resolution than the tracker actually runs at, replay is not a
      // reproduction — feature counts and match quality both shift, and the bug
      // you are chasing may simply not appear. Costs ~1.8x dataset size.
      maxEdge: opts.maxEdge ?? 640,
      jpegQuality: opts.jpegQuality ?? 0.6,
      fps: opts.fps ?? 12,
      maxFrames: opts.maxFrames ?? 900, // ~75s at 12fps
      maxImu: opts.maxImu ?? 4500, // ~150s at 30Hz: longer than the frame cap allows
    };
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('2d context unavailable');
    this.ctx = ctx;
  }

  get isRecording(): boolean {
    return this.recording;
  }

  get frameCount(): number {
    return this.frames.length;
  }

  /** Approximate dataset size in MB (frames dominate). */
  get sizeMb(): number {
    let bytes = 0;
    for (const f of this.frames) bytes += f.image.length * 0.75;
    return bytes / (1024 * 1024);
  }

  start(source: FrameProvider, pose: DevicePose): void {
    if (this.recording) return;
    const size = source.size();
    if (!size) return;

    const scale = Math.min(1, this.opts.maxEdge / Math.max(size.width, size.height));
    this.w = Math.round(size.width * scale);
    this.h = Math.round(size.height * scale);
    this.canvas.width = this.w;
    this.canvas.height = this.h;

    this.frames = [];
    this.imu = [];
    this.t0 = performance.now();
    this.recording = true;
    telemetry.reset();

    this.timer = window.setInterval(() => {
      if (this.frames.length >= this.opts.maxFrames) {
        this.stop();
        return;
      }
      if (!source.drawTo(this.ctx, this.w, this.h)) return;
      try {
        this.frames.push({
          t: performance.now() - this.t0,
          image: this.canvas.toDataURL('image/jpeg', this.opts.jpegQuality),
        });
      } catch {
        /* drop the frame rather than break the run */
      }
    }, 1000 / this.opts.fps);

    // IMU at a higher rate than frames.
    this.imuTimer = window.setInterval(() => {
      if (this.imu.length >= this.opts.maxImu) {
        this.stop();
        return;
      }
      const q = pose.quaternion();
      const l = pose.look();
      this.imu.push({
        t: performance.now() - this.t0,
        quat: { x: q.x, y: q.y, z: q.z, w: q.w },
        yaw: l.yaw,
        pitch: l.pitch,
        roll: l.roll,
      });
    }, 33);
  }

  stop(): void {
    if (this.timer) window.clearInterval(this.timer);
    if (this.imuTimer) window.clearInterval(this.imuTimer);
    this.timer = null;
    this.imuTimer = null;
    this.recording = false;
  }

  build(notes?: string): SlamDataset {
    return {
      version: DATASET_VERSION,
      recordedAt: new Date().toISOString(),
      backend: telemetry.getBackend(),
      device: {
        ua: navigator.userAgent,
        screen: { w: window.screen.width, h: window.screen.height },
        dpr: window.devicePixelRatio,
      },
      frameSize: { width: this.w, height: this.h },
      frames: this.frames,
      imu: this.imu,
      telemetry: telemetry.all(),
      summary: telemetry.summary(),
      notes,
    };
  }

  /** Download the dataset as JSON. */
  export(notes?: string): void {
    const data = this.build(notes);
    const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wintermute-slam-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }

  discard(): void {
    this.frames = [];
    this.imu = [];
  }

  /**
   * Upload the dataset to the server so it can be opened from the replay page on
   * a desktop — no cable, no AirDrop. Gzips client-side (these are ~20MB of
   * base64 JPEG) and reports progress, because this happens over mobile data.
   */
  async upload(
    opts: { label?: string; notes?: string; onProgress?: (fraction: number) => void } = {},
  ): Promise<{ id: string; bytes: number } | { error: string }> {
    if (!API_URL) return { error: 'no server configured (VITE_API_URL unset)' };
    const ds = this.build(opts.notes);
    const json = JSON.stringify(ds);

    let bytes: Uint8Array;
    try {
      bytes = await gzip(json);
    } catch {
      return { error: 'compression failed' };
    }

    const q = new URLSearchParams({
      sessionId: ds.backend + '-' + Date.now().toString(36),
      backend: ds.backend,
      frames: String(ds.frames.length),
      width: String(ds.frameSize.width),
      height: String(ds.frameSize.height),
      durationMs: String(Math.round(ds.summary?.durationMs ?? 0)),
      trackingUptime: String(ds.summary?.trackingUptime ?? 0),
      p95TrackMs: String(ds.summary?.p95TrackMs ?? 0),
      medianFps: String(ds.summary?.medianFps ?? 0),
      label: opts.label ?? '',
      notes: opts.notes ?? '',
    });
    if (UPLOAD_TOKEN) q.set('token', UPLOAD_TOKEN);

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/datasets?${q.toString()}`);
      xhr.setRequestHeader('content-type', 'application/octet-stream');
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) opts.onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve({ error: 'bad server response' });
          }
        } else {
          resolve({ error: `HTTP ${xhr.status}` });
        }
      };
      xhr.onerror = () => resolve({ error: 'network error' });
      // Cast: TS types don't accept a plain Uint8Array here in all lib configs.
      xhr.send(bytes as unknown as XMLHttpRequestBodyInit);
    });
  }
}

/** Gzip a string using CompressionStream, with a clear failure if unsupported. */
async function gzip(text: string): Promise<Uint8Array> {
  const CS = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
  if (!CS) throw new Error('CompressionStream unsupported');
  const stream = new Blob([text]).stream().pipeThrough(new CS('gzip'));
  const buf = await new Response(stream).arrayBuffer();
  return new Uint8Array(buf);
}

/** Decode a dataset's frames into ImageBitmaps for replay. */
export async function decodeDataset(
  ds: SlamDataset,
): Promise<{ bitmaps: ImageBitmap[]; times: number[] }> {
  const bitmaps: ImageBitmap[] = [];
  const times: number[] = [];
  for (const f of ds.frames) {
    try {
      const res = await fetch(f.image);
      const blob = await res.blob();
      bitmaps.push(await createImageBitmap(blob));
      times.push(f.t);
    } catch {
      /* skip undecodable frames */
    }
  }
  return { bitmaps, times };
}
